"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { AddSiteOffcanvas } from "./add"
import { SitesList, Filters } from "./list"
import api from "../../libs/axios"

import { useUrlFilters } from "../../hooks/useUrlFilters";
import { URL_FILTER_KEYS } from "./list";
import { useDebounce } from "../../hooks/useDebounce"

interface Client {
  id: number
  client_name: string
  status: string
}

interface Activity {
  id: number
  activity_name: string
  status: string
}

interface Project {
  id: number
  project_name: string
  status: string
  client?: Client
  activity?: Activity
}

interface User {
  id: number
  first_name: string
  last_name: string
  email: string
  role_id: number
}

interface Site {
  id: number
  site_id: string
  project_id: number
  status: string
  created_at: string
  updated_at: string
  created_by: number
  assigned_engineer_id?: number
  assigned_ta_id?: number
  assigned_report_maker_id?: number
  assigned_validator_id?: number
  visit_remarks?: string
  cancel_reason?: string
  hold_reason?: string
  project?: Project
  activity?: Activity
  site_engineer?: User
  ta?: User
  mis?: User
  report_designer?: User
  validator?: User
  survey_workflow_status: string
  has_pending_billing?: boolean
}

interface SiteSortConfig {
  field: string
  direction: "ASC" | "DESC"
}

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

interface SiteFormData {
  site_id: string
  project_id: number
}

interface EditSiteFormData extends SiteFormData {
  id: number
}

export const SitesManagement: React.FC = () => {
  // State management
  const [sites, setSites] = useState<Site[]>([])
  const [projectSites, setProjectSites] = useState<Site[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [activities, setActivities] = useState<Activity[]>([])
  const [users, setUsers] = useState<{ [roleId: number]: User[] }>({})
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })
  const [sortConfig, setSortConfig] = useState<SiteSortConfig>({
    field: "created_at",
    direction: "DESC",
  })

  // Loading states
  const [sitesLoading, setSitesLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Modal/Offcanvas states
  const [showAddSite, setShowAddSite] = useState(false)
  const [showEditSite, setShowEditSite] = useState(false)
  const [selectedSite, setSelectedSite] = useState<Site | null>(null)

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [projectFilter, setProjectFilter] = useState<string>("")
  const [activityFilter, setActivityFilter] = useState<string>("")
  // Seeded from the URL so a dashboard tile can open this list already
  // filtered to the rows it counted. URL_FILTER_KEYS lives in ./list.
  const [advancedFilters, setAdvancedFilters] = useState<Filters>(
    useUrlFilters<Filters>({}, URL_FILTER_KEYS),
  )
  const [exportFilters, setExportFilters] = useState<Filters>({})

  const navigate = useNavigate()

  // API Functions
  const fetchSites = async (
    search = "",
    status = "",
    project_id = "",
    activity_id = "",
    page = 1,
    limitValue?: number,
    filters: Filters = advancedFilters,
    sort: SiteSortConfig = sortConfig
  ) => {
    setSitesLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const queryParams: any = {
        page: page,
        limit: effectiveLimit,
        ...filters
      }

      if (search.trim()) {
        queryParams.search = search
      }

      if (status.trim()) {
        queryParams.status = status
      }

      if (project_id.trim()) {
        queryParams.project_id = project_id
      }

      if (activity_id.trim()) {
        queryParams.activity_id = activity_id
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key]
        } else {
          queryParams[key] = queryParams[key].toString()
        }
      })

      const response = await api.get("/survey/sites", {
        params: queryParams,
      })

      if (response.data.success) {
        setSites(response.data.data.sites || [])
        setPagination(response.data.data.pagination || pagination)
      } else {
        console.error("API returned success: false")
      }
    } catch (error) {
      console.error("Error fetching sites:", error)
    } finally {
      setSitesLoading(false)
    }
  }
  const fetchProjectSites = async (
    search = "",
    status = "",
    project_id = "",
    activity_id = "",
    page = 1,
    limitValue?: number,
    filters: Filters = advancedFilters,
    sort: SiteSortConfig = sortConfig
  ) => {
    setSitesLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const queryParams: any = {
        page: page,
        limit: effectiveLimit,
        ...filters
      }

      if (search.trim()) {
        queryParams.search = search
      }

      if (status.trim()) {
        queryParams.status = status
      }

      if (project_id.trim()) {
        queryParams.project_id = project_id
      }

      if (activity_id.trim()) {
        queryParams.activity_id = activity_id
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      Object.keys(queryParams).forEach((key) => {
        if (queryParams[key] === undefined || queryParams[key] === null) {
          delete queryParams[key]
        } else {
          queryParams[key] = queryParams[key].toString()
        }
      })

      const response = await api.get("/survey/sites/all", {
        params: queryParams,
      })

      if (response.data.success) {
        setProjectSites(response.data.data.sites || [])
        setPagination(response.data.data.pagination || pagination)
      } else {
        console.error("API returned success: false")
      }
    } catch (error) {
      console.error("Error fetching sites:", error)
    } finally {
      setSitesLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const response = await api.get("/projects/list", {
        params: { limit: 100, status: "ACTIVE" },
      })

      if (response.data.success) {
        setProjects(response.data.data.projects || [])
      }
    } catch (error) {
      console.error("Error fetching projects:", error)
    }
  }

  const fetchActivities = async () => {
    try {
      const response = await api.get("/activities/list", {
        params: { limit: 100, status: "ACTIVE" },
      })

      if (response.data.success) {
        setActivities(response.data.data.activities || [])
      }
    } catch (error) {
      console.error("Error fetching activities:", error)
    }
  }

  const fetchUsersByRole = async (roleId: number) => {
    try {
      const response = await api.get(`/survey/users/role/${roleId}`)

      if (response.data.success) {
        setUsers((prev) => ({
          ...prev,
          [roleId]: response.data.data.users || [],
        }))
      }
    } catch (error) {
      console.error("Error fetching users by role:", error)
    }
  }

  const fetchSiteById = async (siteId: number): Promise<Site | null> => {
    try {
      const response = await api.get(`/survey/sites/${siteId}`)

      if (response.data.success) {
        return response.data.data.site
      }
      return null
    } catch (error) {
      console.error("Error fetching site by ID:", error)
      return null
    }
  }

  const createSite = async (siteData: SiteFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/survey/sites", siteData)

      if (response.data.success) {
        setShowAddSite(false)
        console.log("Site created successfully")
      } else {
        console.error("Failed to create site:", response.data.message)
      }
    } catch (error) {
      console.error("Error creating site:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const assignEngineer = async (siteId: number, data: any) => {
    setActionLoading(true)
    try {
      const response = await api.post(`/survey/sites/${siteId}/assign-engineer`, data)

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log("Engineer assigned successfully")
      } else {
        console.error("Failed to assign engineer:", response.data.message)
      }
    } catch (error) {
      console.error("Error assigning engineer:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateAssignment = async (siteId: number, data: any, assignmentType: string) => {
    setActionLoading(true)
    try {
      const payload: any = {
        site_ids: [siteId],
      }

      // Map assignment type to the correct field name
      switch (assignmentType) {
        case 'site_engineer':
          payload.site_engineer_userid = data.user.value
          break
        case 'ta':
          payload.ta_userid = data.user.value
          break
        case 'mis':
          payload.mis_userid = data.user.value
          break
        case 'report_maker':
          payload.report_designer_userid = data.user.value
          break
        case 'validator':
          payload.validator_userid = data.user.value
          break
      }

      const response = await api.put('/survey/sites/update-assignment', payload)

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log(`${assignmentType} assigned successfully`)
      } else {
        console.error(`Failed to assign ${assignmentType}:`, response.data.message)
      }
    } catch (error) {
      console.error(`Error assigning ${assignmentType}:`, error)
    } finally {
      setActionLoading(false)
    }
  }

  const assignReportMaker = async (siteId: number, data: any) => {
    setActionLoading(true)
    try {
      const response = await api.post(`/survey/sites/${siteId}/assign-report-maker`, data)

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log("Report maker assigned successfully")
      } else {
        console.error("Failed to assign report maker:", response.data.message)
      }
    } catch (error) {
      console.error("Error assigning report maker:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const assignValidator = async (siteId: number, data: any) => {
    setActionLoading(true)
    try {
      const response = await api.post(`/survey/sites/${siteId}/assign-validator`, data)

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log("Validator assigned successfully")
      } else {
        console.error("Failed to assign validator:", response.data.message)
      }
    } catch (error) {
      console.error("Error assigning validator:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const cancelSite = async (siteId: number, cancelReason: string) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/survey/sites/${siteId}/cancel`, {
        cancel_reason: cancelReason,
      })

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log("Site cancelled successfully")
      } else {
        console.error("Failed to cancel site:", response.data.message)
      }
    } catch (error) {
      console.error("Error cancelling site:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const holdSite = async (siteId: number, holdReason: string) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/survey/sites/${siteId}/hold`, {
        hold_reason: holdReason,
      })

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log("Site put on hold successfully")
      } else {
        console.error("Failed to put site on hold:", response.data.message)
      }
    } catch (error) {
      console.error("Error putting site on hold:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const resumeSite = async (siteId: number) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/survey/sites/${siteId}/resume`)

      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
        console.log("Site resumed successfully")
      } else {
        console.error("Failed to resume site:", response.data.message)
      }
    } catch (error) {
      console.error("Error resuming site:", error)
    } finally {
      setActionLoading(false)
    }
  }

  // Event handlers
  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchSites(debouncedSearch, statusFilter, projectFilter, activityFilter, 1, undefined, advancedFilters, sortConfig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchSites(searchTerm, status, projectFilter, activityFilter, 1, undefined, advancedFilters, sortConfig)
  }

  const handleProjectFilter = (project_id: string) => {
    setProjectFilter(project_id)
    fetchSites(searchTerm, statusFilter, project_id, activityFilter, 1, undefined, advancedFilters, sortConfig)
  }

  const handleActivityFilter = (activity_id: string) => {
    setActivityFilter(activity_id)
    fetchSites(searchTerm, statusFilter, projectFilter, activity_id, 1, undefined, advancedFilters, sortConfig)
  }

  const handleAdvancedFilter = (filters: Filters) => {
    setAdvancedFilters(filters)
    setExportFilters(filters)
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, 1, undefined, filters, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, page, undefined, advancedFilters, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, 1, limit, advancedFilters, sortConfig)
  }

  const handleSort = (field: string, direction: "ASC" | "DESC") => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, pagination.page, undefined, advancedFilters, sort)
  }

  const handleAddSite = () => {
    // setShowAddSite(true)
    navigate("/sites/create")
  }

  const handleEditSite = async (site: Site) => {
    const fullSite = await fetchSiteById(site.id)
    if (fullSite) {
      setSelectedSite(fullSite)
      setShowEditSite(true)
    }
  }

  const handleAssignEngineer = (site: Site, data: any) => {
    // If engineer already exists, use update assignment, otherwise use the assign endpoint
    if (site.site_engineer) {
      updateAssignment(site.id, data, 'site_engineer')
    } else {
      assignEngineer(site.id, data)
    }
  }

  const handleAssignTA = (site: Site, data: any) => {
    updateAssignment(site.id, data, 'ta')
  }

  const handleAssignMIS = (site: Site, data: any) => {
    updateAssignment(site.id, data, 'mis')
  }

  const handleAssignReportMaker = (site: Site, data: any) => {
    // If report maker already exists, use update assignment, otherwise use the assign endpoint
    if (site.report_designer) {
      updateAssignment(site.id, data, 'report_maker')
    } else {
      assignReportMaker(site.id, data)
    }
  }

  const handleAssignValidator = (site: Site, data: any) => {
    // If validator already exists, use update assignment, otherwise use the assign endpoint
    if (site.validator) {
      updateAssignment(site.id, data, 'validator')
    } else {
      assignValidator(site.id, data)
    }
  }

  const handleCancelSite = (site: Site, reason: string) => {
    cancelSite(site.id, reason)
  }

  const handleHoldSite = (site: Site, reason: string) => {
    holdSite(site.id, reason)
  }

  const handleResumeSite = (site: Site) => {
    resumeSite(site.id)
  }

  const handleBulkAssign = async (siteIds: number[], data: any, type: "reportMaker" | "validator") => {
    setActionLoading(true)
    try {
      const payload: any = { site_ids: siteIds }
      if (type === "reportMaker") {
        payload.report_designer_userid = data.user.value
      } else {
        payload.validator_userid = data.user.value
      }
      const response = await api.put('/survey/sites/update-assignment', payload)
      if (response.data.success) {
        await fetchSites(searchTerm, statusFilter, projectFilter, activityFilter)
      }
    } catch (error) {
      console.error(`Error bulk assigning ${type}:`, error)
    } finally {
      setActionLoading(false)
    }
  }

  const handleCloseModals = () => {
    setShowAddSite(false)
    setShowEditSite(false)
    setSelectedSite(null)
  }

  // Initialize data on component mount
  useEffect(() => {
    setExportFilters(advancedFilters)
    fetchSites(searchTerm, statusFilter, projectFilter, activityFilter, pagination.page, undefined, advancedFilters, sortConfig)
    fetchProjects()
    fetchActivities()
    // Fetch users for different roles
    fetchUsersByRole(2) // Engineers
    fetchUsersByRole(5) // Report Makers
    fetchUsersByRole(6) // Validators
  }, [])

  return (
    <div className="">
      <div className="">
        {/* Main Sites List */}
        <SitesList
          sites={sites}
          projects={projects}
          activities={activities}
          users={users}
          pagination={pagination}
          onSearch={handleSearch}
          onStatusFilter={handleStatusFilter}
          onProjectFilter={handleProjectFilter}
          onActivityFilter={handleActivityFilter}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onSort={handleSort}
          onAddSite={handleAddSite}
          onEditSite={handleEditSite}
          onAssignEngineer={handleAssignEngineer}
          onAssignTA={handleAssignTA}
          onAssignMIS={handleAssignMIS}
          onAssignReportMaker={handleAssignReportMaker}
          onAssignValidator={handleAssignValidator}
          onCancelSite={handleCancelSite}
          onHoldSite={handleHoldSite}
          onResumeSite={handleResumeSite}
          onAdvancedFilter={handleAdvancedFilter}
          onBulkAssign={handleBulkAssign}
          loading={sitesLoading}
          actionLoading={actionLoading}
        />

        {/* Add Site Offcanvas */}
        <AddSiteOffcanvas
          isOpen={showAddSite}
          onClose={handleCloseModals}
          onSubmit={createSite}
          projects={projects}
          loading={actionLoading}
        />

      </div>
    </div>
  )
}
